import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="spots-map"
export default class extends Controller {
  static targets = ["map"];

  connect() {
    this.showLoading();
    this.loadSpots();
  }

  showLoading() {
    const spinner = document.getElementById("spots-map-loading");
    if (spinner) spinner.classList.remove("hidden");
  }

  hideLoading() {
    const spinner = document.getElementById("spots-map-loading");
    if (spinner) spinner.classList.add("hidden");
  }

  async loadSpots() {
    this.showLoading();
    // Build the JSON URL with current filters
    const url = new URL(window.location.href);
    url.searchParams.set("format", "json");
    const response = await fetch(url.toString());
    const spots = await response.json();
    this.initMap(spots);
    this.hideLoading();
  }

  initMap(spots) {
    if (!window.L) {
      console.error("Leaflet is not loaded");
      this.hideLoading();
      return;
    }
    // Remove any existing map instance
    if (this._map) {
      this._map.remove();
    }
    // Center map based on spots or default
    let center = [0, 0];
    if (spots.length > 0) {
      const lats = spots.map((s) => parseFloat(s.latitude));
      const lngs = spots.map((s) => parseFloat(s.longitude));
      center = [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2,
      ];
    }
    this._map = window.L.map(this.mapTarget).setView(
      center,
      spots.length > 0 ? 3 : 2
    );
    window.L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles © Esri — Source: Esri, USGS, NOAA",
        noWrap: false,
        minZoom: 2,
        maxZoom: 18,
      }
    ).addTo(this._map);
    window.L.tileLayer(
      "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Labels © Esri",
        noWrap: false,
        minZoom: 2,
        maxZoom: 18,
      }
    ).addTo(this._map);

    // Add markers
    spots.forEach((spot) => {
      if (!spot.latitude || !spot.longitude) return;
      const marker = window.L.marker([spot.latitude, spot.longitude]).addTo(
        this._map
      );
      let popupHtml = `
        <div class='bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border-2 border-primary/70 min-w-[240px] max-w-[340px] p-0 overflow-hidden'>
          <div class='bg-primary flex items-center gap-2 px-4 py-2'>
            <span class='font-extrabold text-lg text-primary-content tracking-tight'>${
              spot.name
            }</span>
          </div>
          ${
            spot.photo_url
              ? `<img src='${spot.photo_url}' class='w-full h-32 object-cover border-b border-primary/30 shadow-sm' alt='${spot.name}' />`
              : ""
          }
          <div class='p-4 space-y-1 text-base-content text-sm'>
            ${
              spot.region
                ? `<div><span class='font-semibold'>Region:</span> <span>${spot.region}</span></div>`
                : ""
            }
            ${
              spot.towns
                ? `<div><span class='font-semibold'>Nearby Towns:</span> <span>${spot.towns}</span></div>`
                : ""
            }
            ${
              spot.kind
                ? `<div><span class='font-semibold'>Type:</span> <span>${spot.kind}</span></div>`
                : ""
            }
            ${
              spot.ability
                ? `<div><span class='font-semibold'>Ability:</span> <span>${spot.ability}</span></div>`
                : ""
            }
            ${
              spot.bottom
                ? `<div><span class='font-semibold'>Bottom:</span> <span>${spot.bottom}</span></div>`
                : ""
            }
            ${
              spot.consistency
                ? `<div><span class='font-semibold'>Consistency:</span> <span>${spot.consistency}</span></div>`
                : ""
            }
            ${
              spot.crowds
                ? `<div><span class='font-semibold'>Crowds:</span> <span>${spot.crowds}</span></div>`
                : ""
            }
            ${
              spot.direction
                ? `<div><span class='font-semibold'>Direction:</span> <span>${spot.direction}</span></div>`
                : ""
            }
            ${
              spot.length_meters
                ? `<div><span class='font-semibold'>Wave Length:</span> <span>${spot.length_meters}</span></div>`
                : ""
            }
            ${
              spot.rating
                ? `<div><span class='font-semibold'>Rating:</span> <span>${spot.rating}</span></div>`
                : ""
            }
            ${
              spot.size_min_ft
                ? `<div><span class='font-semibold'>Min Size (ft):</span> <span>${spot.size_min_ft}</span></div>`
                : ""
            }
            ${
              spot.size_max_ft
                ? `<div><span class='font-semibold'>Max Size (ft):</span> <span>${spot.size_max_ft}</span></div>`
                : ""
            }
            ${
              spot.swell_direction
                ? `<div><span class='font-semibold'>Swell Direction:</span> <span>${spot.swell_direction}</span></div>`
                : ""
            }
            ${
              spot.wind
                ? `<div><span class='font-semibold'>Wind:</span> <span>${spot.wind}</span></div>`
                : ""
            }
            ${
              spot.tide
                ? `<div><span class='font-semibold'>Tide:</span> <span>${spot.tide}</span></div>`
                : ""
            }
            ${
              spot.hotspot
                ? `<div><span class='font-semibold text-accent'>Hotspot</span></div>`
                : ""
            }
            ${
              spot.excerpt
                ? `<div class='italic text-xs text-gray-500 dark:text-gray-400 mt-1'>${spot.excerpt}</div>`
                : ""
            }
            ${
              spot.description
                ? `<div class='mt-2 text-xs text-gray-700 dark:text-gray-300'>${spot.description}</div>`
                : ""
            }
          </div>
          <div class='px-4 pb-3 flex justify-end'>
            ${
              spot.stormrider_slug
                ? `<a href='https://stormrider.surf/${spot.stormrider_slug}' target='_blank' rel='noopener' class='btn btn-sm btn-accent font-bold rounded-full px-4'>View on Stormrider</a>`
                : ""
            }
          </div>
        </div>
      `;
      marker.bindPopup(popupHtml);
    });

    // Fit bounds if there are spots
    if (spots.length > 0) {
      const bounds = window.L.latLngBounds(
        spots.map((s) => [s.latitude, s.longitude])
      );
      this._map.fitBounds(bounds, { padding: [30, 30], maxZoom: 7 });
    }
    this.hideLoading();
  }
}
